import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { usePortalUsers } from '@/hooks/usePortalUsers';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { 
  UserPlus, 
  Search, 
  MoreVertical, 
  Edit, 
  UserX, 
  UserCheck,
  Shield
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PortalUserModal } from '@/components/crm/PortalUserModal';
import { ProjectAssignmentModal } from '@/components/crm/ProjectAssignmentModal';
import { useProjectAssignments } from '@/hooks/useProjectAssignments';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { FolderKanban } from 'lucide-react';

export default function CRMPortalUsers() {
  const { hasRole } = useAuth();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
  const [selectedUserForAssignment, setSelectedUserForAssignment] = useState<any>(null);

  const { data: portalUsers, isLoading, refetch } = usePortalUsers(
    roleFilter === 'all' ? undefined : roleFilter
  );

  if (!hasRole('super_admin')) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="p-12 text-center">
            <Shield className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-2xl font-bold mb-2">Geen toegang</h2>
            <p className="text-muted-foreground">
              Alleen super administrators kunnen portal gebruikers beheren.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const filteredUsers = portalUsers?.filter(user =>
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleToggleActive = async (userId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('portal_users')
        .update({ is_active: !currentStatus })
        .eq('id', userId);

      if (error) throw error;

      toast({
        title: currentStatus ? 'Gebruiker gedeactiveerd' : 'Gebruiker geactiveerd',
        description: 'De status is succesvol bijgewerkt.',
      });

      refetch();
    } catch (error) {
      console.error('Error toggling user status:', error);
      toast({
        title: 'Fout',
        description: 'Kon gebruikersstatus niet bijwerken',
        variant: 'destructive',
      });
    }
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'inspector':
        return 'default';
      case 'client':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const getRoleDisplayName = (role: string) => {
    switch (role) {
      case 'inspector':
        return 'Inspecteur';
      case 'client':
        return 'Klant';
      default:
        return role;
    }
  };

  const getInitials = (email: string) => {
    return email.substring(0, 2).toUpperCase();
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Portal Gebruikers</h1>
          <p className="text-muted-foreground">
            Beheer inspecteurs en klant portal accounts
          </p>
        </div>
        <Button onClick={() => { setSelectedUser(null); setModalOpen(true); }}>
          <UserPlus className="mr-2 h-4 w-4" />
          Nieuwe Portal Gebruiker
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Zoek op email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="Filter op rol" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle rollen</SelectItem>
                <SelectItem value="inspector">Inspecteurs</SelectItem>
                <SelectItem value="client">Klanten</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Laden...</p>
            </div>
          ) : filteredUsers && filteredUsers.length > 0 ? (
            <div className="space-y-4">
              {filteredUsers.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                >
                  <div className="flex items-center gap-4 flex-1">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-primary/10 text-primary">
                        {getInitials(user.email)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{user.email}</p>
                        {!user.is_active && (
                          <Badge variant="outline" className="text-xs">
                            Inactief
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant={getRoleBadgeVariant(user.role)}>
                          {getRoleDisplayName(user.role)}
                        </Badge>
                        {user.last_login_at && (
                          <span className="text-xs text-muted-foreground">
                            Laatste login: {new Date(user.last_login_at).toLocaleDateString('nl-NL')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedUserForAssignment(user);
                      setAssignmentModalOpen(true);
                    }}
                  >
                    <FolderKanban className="mr-2 h-4 w-4" />
                    Projecten Beheren
                  </Button>
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => {
                          setSelectedUser(user);
                          setModalOpen(true);
                        }}
                      >
                        <Edit className="mr-2 h-4 w-4" />
                        Bewerken
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleToggleActive(user.id, user.is_active)}
                      >
                        {user.is_active ? (
                          <>
                            <UserX className="mr-2 h-4 w-4" />
                            Deactiveren
                          </>
                        ) : (
                          <>
                            <UserCheck className="mr-2 h-4 w-4" />
                            Activeren
                          </>
                        )}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <UserPlus className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">Geen portal gebruikers gevonden</p>
            </div>
          )}
        </CardContent>
      </Card>

      <PortalUserModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        user={selectedUser}
        onSuccess={refetch}
      />

      {selectedUserForAssignment && (
        <ProjectAssignmentModal
          open={assignmentModalOpen}
          onOpenChange={setAssignmentModalOpen}
          portalUser={selectedUserForAssignment}
        />
      )}
    </div>
  );
}
